<template>
  <div class="drp-tabs" v-click-outside="closeDropdown">
    <!-- Pré-evento: estado especial — só "Personalizado" e nota explicativa -->
    <template v-if="eventStatus === 'upcoming'">
      <div class="drp-event-note drp-event-note-upcoming" :title="upcomingTooltip">
        <i class="bx bx-time-five"></i>
        Evento começa em {{ daysUntilEvent }} {{ daysUntilEvent === 1 ? 'dia' : 'dias' }}
      </div>
      <span class="drp-tab-sep"></span>
    </template>

    <!-- Sempre disponível enquanto faz sentido (ongoing/finished/sem evento) -->
    <button
      v-if="showLastHour"
      type="button"
      class="drp-tab"
      :class="{ active: isCurrentPeriod('last_hour') }"
      @click="selectQuick('last_hour')"
      title="Últimos 60 minutos a partir de agora"
    >Última hora</button>

    <!-- Single-day OU "hoje genérico" sem evento -->
    <button
      v-if="showToday"
      type="button"
      class="drp-tab"
      :class="{ active: isCurrentPeriod('today') }"
      @click="selectQuick('today')"
      title="Do início do dia até agora"
    >{{ todayLabel }}</button>

    <!-- Multi-dia: chip por dia do evento -->
    <template v-if="showEventDays">
      <button
        v-for="day in event.days"
        :key="'day-' + day.index"
        type="button"
        class="drp-tab drp-tab-day"
        :class="{
          active: isEventDay(day.index),
          'drp-tab-day-future': day.date > event.today,
          'drp-tab-day-current': day.date === event.today
        }"
        @click="selectEventDay(day.index)"
        :title="dayTooltip(day)"
      >
        <span class="drp-day-idx">D{{ day.index }}</span>
        <span class="drp-day-date">{{ formatShortDate(day.date) }}</span>
      </button>
    </template>

    <!-- Evento todo (multi-dia ou pós-evento) -->
    <button
      v-if="showEventAll"
      type="button"
      class="drp-tab"
      :class="{ active: isCurrentPeriod('event_all') }"
      @click="selectQuick('event_all')"
      title="Todo o período do evento configurado"
    >
      <i class="bx bx-calendar-event drp-tab-icon"></i>
      Evento todo
    </button>

    <!-- 7 dias só faz sentido sem evento configurado (visão genérica) -->
    <button
      v-if="showSevenDays"
      type="button"
      class="drp-tab"
      :class="{ active: isCurrentPeriod('7d') }"
      @click="selectQuick('7d')"
    >7 dias</button>

    <span class="drp-tab-sep"></span>

    <div class="drp-picker-wrap">
      <button
        type="button"
        class="drp-tab"
        :class="{ active: isCustomActive }"
        @click="showPicker = !showPicker"
        :title="customTooltip"
      ><i class="mdi mdi-calendar-range"></i></button>

      <div v-if="showPicker" class="drp-pop">
        <div v-if="event && event.init_date" class="drp-pop-hint">
          <i class="bx bx-info-circle"></i>
          Range limitado às datas do evento
          ({{ formatShortDate(event.init_date) }} – {{ formatShortDate(event.end_date) }})
        </div>
        <label class="drp-pop-label">De</label>
        <input
          type="date"
          v-model="customStart"
          class="drp-pop-input"
          :max="customEnd || customMaxDate"
          :min="customMinDate"
        />
        <label class="drp-pop-label">Até</label>
        <input
          type="date"
          v-model="customEnd"
          class="drp-pop-input"
          :min="customStart || customMinDate"
          :max="customMaxDate"
        />
        <div class="drp-pop-actions">
          <button type="button" class="drp-pop-btn drp-pop-cancel" @click="clearCustom">Limpar</button>
          <button
            type="button"
            class="drp-pop-btn drp-pop-apply"
            :disabled="!canApply"
            @click="applyCustom"
          >Aplicar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const clickOutside = {
  bind (el, binding) {
    el.__clickOutsideHandler__ = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value(event)
      }
    }
    document.addEventListener('click', el.__clickOutsideHandler__)
  },
  unbind (el) {
    document.removeEventListener('click', el.__clickOutsideHandler__)
  }
}

const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

export default {
  name: 'date-range-picker',
  directives: { 'click-outside': clickOutside },
  props: {
    period: { type: String, default: 'today' },
    from: { type: Date, default: null },
    to: { type: Date, default: null },
    /**
     * `active_dates` (do backend) é só usado pra LIMITAR o range do picker
     * (min/max) quando NÃO há evento configurado. Operador não consegue
     * escolher dias sem leitura. Quando há evento, init_date/end_date dele
     * têm precedência.
     */
    activeDates: { type: Array, default: () => [] },
    /**
     * Contexto do evento — vem do backend via filterOptions.event.
     * Quando presente, o picker se reorganiza: 1 dia → chips simples;
     * multi-dia → chips por dia (D1, D2…); pré → bloqueado; pós → "Evento
     * todo" como default. null = comportamento genérico (sem evento).
     */
    event: { type: Object, default: null }
  },
  data: () => ({
    showPicker: false,
    customStart: '',
    customEnd: ''
  }),
  computed: {
    eventStatus () {
      return (this.event && this.event.status) || 'unconfigured'
    },
    isSingleDay () {
      return this.event && Array.isArray(this.event.days) && this.event.days.length === 1
    },
    isMultiDay () {
      return this.event && Array.isArray(this.event.days) && this.event.days.length > 1
    },
    /**
     * "Última hora" só faz sentido quando há atividade real ou prevista — em
     * eventos finalizados deixa de ser útil (todos os números são fixos).
     */
    showLastHour () {
      return this.eventStatus !== 'finished'
    },
    /**
     * "Hoje" tem sentido em:
     *  - sem evento configurado (visão genérica)
     *  - evento single-day E hoje É o dia do evento (== "Evento todo")
     *    → escondemos pra evitar duplicar com "Evento todo"
     *  - evento multi-dia em ANDAMENTO: cabe junto com os Dx, mas seria
     *    redundante com o chip do dia atual; escondemos.
     */
    showToday () {
      if (this.eventStatus === 'unconfigured') return true
      // Single-day fora do dia do evento (raro): mostra hoje genérico
      if (this.isSingleDay) {
        const day = this.event.days[0]
        return day.date !== this.event.today
      }
      return false
    },
    todayLabel () {
      return this.eventStatus === 'unconfigured' ? 'Hoje' : 'Hoje'
    },
    showEventDays () {
      return this.isMultiDay
    },
    /**
     * "Evento todo" só aparece no multi-dia. No single-day o chip do dia
     * já cumpre essa função. No pré-evento, sem motivo (não tem dado).
     */
    showEventAll () {
      return this.isMultiDay && this.eventStatus !== 'upcoming'
    },
    showSevenDays () {
      return this.eventStatus === 'unconfigured'
    },
    isCustomActive () {
      return this.period === 'custom' || this.period === 'single_day'
    },
    canApply () {
      return this.customStart && this.customEnd && this.customStart <= this.customEnd
    },
    customMinDate () {
      // Prioridade: datas do evento (cadastradas) > active_dates (com leitura)
      if (this.event && this.event.init_date) return this.event.init_date
      if (!this.activeDates || this.activeDates.length === 0) return ''
      return this.activeDates[0].date
    },
    customMaxDate () {
      if (this.event && this.event.end_date) return this.event.end_date
      if (!this.activeDates || this.activeDates.length === 0) return ''
      return this.activeDates[this.activeDates.length - 1].date
    },
    customTooltip () {
      if (this.event && this.event.init_date) {
        return `Período personalizado dentro do evento (${this.formatShortDate(this.event.init_date)} – ${this.formatShortDate(this.event.end_date)})`
      }
      return 'Período personalizado'
    },
    daysUntilEvent () {
      if (!this.event || !this.event.init_date || !this.event.today) return 0
      const start = new Date(this.event.init_date + 'T00:00:00')
      const today = new Date(this.event.today + 'T00:00:00')
      return Math.max(0, Math.round((start.getTime() - today.getTime()) / 86400000))
    },
    upcomingTooltip () {
      return `Evento começa em ${this.event.init_date} e vai até ${this.event.end_date}`
    }
  },
  watch: {
    showPicker (val) {
      if (val) {
        this.customStart = this.from ? this.toIsoDate(this.from) : ''
        this.customEnd = this.to ? this.toIsoDate(this.to) : ''
      }
    }
  },
  methods: {
    isCurrentPeriod (value) {
      return this.period === value
    },
    isEventDay (idx) {
      return this.period === `event_day_${idx}`
    },
    selectQuick (value) {
      this.$emit('change', { period: value })
      this.showPicker = false
    },
    selectEventDay (idx) {
      this.$emit('change', { period: `event_day_${idx}` })
      this.showPicker = false
    },
    applyCustom () {
      if (!this.canApply) return
      const isSingle = this.customStart === this.customEnd
      const start = new Date(this.customStart + 'T00:00:00')
      const end = new Date(this.customEnd + 'T23:59:59')
      this.$emit('change', {
        period: isSingle ? 'single_day' : 'custom',
        from: start,
        to: end
      })
      this.showPicker = false
    },
    clearCustom () {
      this.customStart = ''
      this.customEnd = ''
      // Reset pra default razoável conforme contexto
      const fallback = this.isMultiDay
        ? (this.eventStatus === 'finished' ? 'event_all' : 'last_hour')
        : 'today'
      this.$emit('change', { period: fallback })
      this.showPicker = false
    },
    closeDropdown () {
      this.showPicker = false
    },
    toIsoDate (d) {
      if (!d) return ''
      const dt = d instanceof Date ? d : new Date(d)
      return [
        dt.getFullYear(),
        String(dt.getMonth() + 1).padStart(2, '0'),
        String(dt.getDate()).padStart(2, '0')
      ].join('-')
    },
    formatShortDate (iso) {
      if (!iso) return ''
      const [y, m, d] = iso.split('-').map(s => parseInt(s, 10))
      if (!y || !m || !d) return iso
      return `${String(d).padStart(2, '0')} ${MONTHS_SHORT[m - 1]}`
    },
    dayTooltip (day) {
      const isToday = day.date === this.event.today
      const isFuture = day.date > this.event.today
      const base = `Dia ${day.index} do evento — ${this.formatShortDate(day.date)}`
      if (isToday) return `${base} · acontecendo hoje`
      if (isFuture) return `${base} · ainda não chegou`
      return `${base} · já aconteceu`
    }
  }
}
</script>

<style lang="scss" scoped>
/* Espelha o padrão `.sa-tabs` do EventDashboard pra consistência visual */
.drp-tabs {
  display: inline-flex;
  align-items: center;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 8px;
  padding: 3px;
  height: 38px;
  gap: 1px;
  flex-wrap: wrap;
}

.drp-tab {
  background: transparent;
  border: 0;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #4a5068;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;

  i { font-size: 16px; }

  &:hover { background: #f5f6fa; }

  &.active {
    background: #556ee6;
    color: #fff;
    box-shadow: 0 2px 8px rgba(85, 110, 230, 0.3);
  }
}

.drp-tab-icon {
  font-size: 14px !important;
  margin-right: 2px;
}

/* Chips de dia do evento (D1, D2…) — denso, fonte menor pro index */
.drp-tab-day {
  flex-direction: column;
  gap: 0;
  padding: 4px 10px;
  line-height: 1.1;

  .drp-day-idx {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8b90a5;
  }

  .drp-day-date {
    font-size: 11px;
    font-weight: 600;
    color: #1a1d26;
  }

  &.active {
    .drp-day-idx { color: rgba(255, 255, 255, 0.85); }
    .drp-day-date { color: #fff; }
  }

  /* Indicador discreto do "dia de hoje" — pontinho verde — quando NÃO ativo */
  &.drp-tab-day-current:not(.active) {
    position: relative;
    .drp-day-idx { color: #34c38f; }

    &::after {
      content: '';
      position: absolute;
      top: 4px;
      right: 4px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #34c38f;
      box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.6);
      animation: drp-day-pulse 1.8s ease-out infinite;
    }
  }

  /* Dias futuros (ainda não aconteceram) — escurecidos pra indicar */
  &.drp-tab-day-future:not(.active) {
    opacity: 0.5;

    .drp-day-idx, .drp-day-date { color: #8b90a5; }
  }
}

@keyframes drp-day-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0.6); }
  70%  { box-shadow: 0 0 0 5px rgba(52, 195, 143, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 195, 143, 0); }
}

.drp-tab-sep {
  width: 1px;
  height: 20px;
  background: #dee2e6;
  margin: 0 2px;
}

/* Aviso de "evento começa em X dias" — não é um botão clicável */
.drp-event-note {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  height: 30px;

  i { font-size: 14px; }
}

.drp-event-note-upcoming {
  background: rgba(241, 180, 76, 0.1);
  color: #b8801f;
}

.drp-picker-wrap {
  position: relative;
}

.drp-pop {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 240px;
  background: #fff;
  border: 1px solid #eaecf2;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(26, 29, 38, 0.12);
  padding: 12px;
  z-index: 1000;
  animation: drp-pop-in 0.15s ease-out;
}

@keyframes drp-pop-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}

.drp-pop-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: #556ee6;
  background: rgba(85, 110, 230, 0.06);
  padding: 6px 8px;
  border-radius: 6px;
  margin-bottom: 10px;
  line-height: 1.4;

  i { font-size: 12px; flex-shrink: 0; }
}

.drp-pop-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8b90a5;
  margin: 0 0 4px 0;
}

.drp-pop-input {
  width: 100%;
  height: 34px;
  padding: 0 10px;
  border: 1px solid #eaecf2;
  border-radius: 6px;
  font-size: 12px;
  color: #1a1d26;
  background: #fff;
  margin-bottom: 8px;

  &:focus {
    outline: none;
    border-color: #556ee6;
    box-shadow: 0 0 0 3px rgba(85, 110, 230, 0.1);
  }
}

.drp-pop-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
}

.drp-pop-btn {
  height: 30px;
  padding: 0 14px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
}

.drp-pop-cancel {
  background: #fff;
  border: 1px solid #eaecf2;
  color: #4a5068;

  &:hover { background: #f5f6fa; }
}

.drp-pop-apply {
  background: #556ee6;
  border: 0;
  color: #fff;

  &:hover { background: #4055c8; }
  &:disabled {
    background: #d0d5dd;
    cursor: not-allowed;
  }
}
</style>
